import React, { useState, useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { makeRequest2 } from '../../../../../../utils/makeRequest';
import { smart_meters_base_url } from "../../../../../../utils/urls";
import { toastify } from '../../../../../../utils/toast';

const DailyMeterReadingHistory = ({ meterId }) => {
    // State for readings, loading status, and selected dates
    const [readings, setReadings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [rows, setRows] = useState(10);
    const today = new Date().toISOString().split('T')[0];
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    // Pagination options
    const rowsPerPageOptions = [10, 20, 50, 100];

    // State for all readings and filtered readings
    const [allReadings, setAllReadings] = useState([]);

    // Fetch all readings data when meterId changes
    useEffect(() => {
        fetchAllReadings();
    }, [meterId]); 

    useEffect(() => {
        filterReadings();
    }, [allReadings, startDate, endDate]);

    const fetchAllReadings = async () => {
        if (!meterId) {
            setAllReadings([]);
            setReadings([]);
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            const url = `${smart_meters_base_url}/daily_readings/${meterId}`;
            const response = await makeRequest2(url, 'GET', null);

            if (response.success) {
                // Store all readings
                const historyData = response.data.history || [];
                setAllReadings(historyData);
            } else {
                toastify('Failed to fetch meter reading history', 'error');
                setAllReadings([]);
                setReadings([]);
            }
        } catch (error) {
            toastify('Error fetching meter reading history', 'error');
            setAllReadings([]);
            setReadings([]);
        } finally {
            setLoading(false);
        }
    };

    // Filter readings based on selected date range
    const filterReadings = () => {
        if (!allReadings.length) {
            setReadings([]);
            return;
        }

        // If both dates are empty, show all readings
        if (!startDate && !endDate) {
            setReadings(allReadings);
            return;
        }

        const start = startDate ? new Date(startDate) : null;
        const end = endDate ? new Date(endDate) : null;

        if (end) {
            end.setHours(23, 59, 59, 999); 
        }

        const filtered = allReadings.filter(reading => {
            const readingDate = new Date(reading.date);
            if (start && readingDate < start) return false;
            if (end && readingDate > end) return false;
            return true;
        });

        setReadings(filtered);
    };

    // Handle exporting data
    const handleExport = () => {
        // CSV header
        let csvContent = "Date,Reading (m³),Status\n";

        // Add rows
        readings.forEach(row => {
            csvContent += `${row.date},${row.reading},${row.status || 'N/A'}\n`;
        });

        // Create download link
        const encodedUri = encodeURI("data:text/csv;charset=utf-8," + csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `meter_${meterId}_readings_${startDate}_to_${endDate}.csv`);
        document.body.appendChild(link);

        // Download the CSV file
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div className="row">
            <div className="col-sm-12">
                <div className="card">
                    <div className="card-header">
                        <div className="row">
                            {/* Date Filter Section */}
                            <div className="col-md-4">
                                <label>Start Date</label>
                                <input
                                    type="date"
                                    className="form-control"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    max={endDate}
                                />
                            </div>
                            <div className="col-md-4">
                                <label>End Date</label>
                                <input
                                    type="date"
                                    className="form-control"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    min={startDate}
                                />
                            </div>
                            <div className="col-md-4 d-flex align-items-end">
                                <Button
                                    label="Filter"
                                    onClick={filterReadings}
                                    className="btn btn-primary me-2"
                                    disabled={loading}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="card-body">
                        <DataTable
                            value={readings}
                            loading={loading}
                            paginator
                            rows={rows}
                            rowsPerPageOptions={rowsPerPageOptions}
                            emptyMessage="No readings available for the selected date range"
                            stripedRows
                        >
                            <Column
                                field="date"
                                header="Date"
                                sortable
                            />
                            <Column
                                field="reading"
                                header="Reading (m³)"
                                sortable
                            />
                            <Column
                                field="status"
                                header="Status"
                                body={(rowData) => (
                                    <span className={`badge bg-${rowData.status === 'opened' ? 'success' : 'warning'}`}>
                                        {rowData.status || 'N/A'}
                                    </span>
                                )}
                                sortable
                            />
                        </DataTable>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DailyMeterReadingHistory;